import { Resources } from 'constants/resources';

import { differenceInDays } from 'date-fns';
import { FILTER_RESET_VALUES } from 'pages/Monitoring/OrderConversionChart/constants/filterResetValues';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { required, useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import {
  DateRangeTimezoneValue,
  DateRangeValue,
} from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { FilterBar, FilterBarActions, FilterBarRow } from 'shared/mui/Filter/FilterBar';
import { FilterApplyButton, FilterResetButton } from 'shared/mui/Filter/FilterButtons';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  DateTimeRangeTimezoneInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { convertReactAdminIdToHydraId, getId } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';

export type OrderConversionChartFilterValues = {
  appliedDate?: DateRangeValue;
  timezone?: DateRangeTimezoneValue;
  merchantId: string | null;
  providerIds: string[];
};

export const OrderConversionChartFilter: React.FC = () => {
  const translate = useTypedTranslate();
  const { filterValues } = useListContext();

  const { form, appliedFilters, onSubmit, onReset } = useFilters<OrderConversionChartFilterValues>({
    mode: 'all',
    defaultValues: {
      appliedDate: filterValues?.appliedDate ?? [null, null],
      timezone: filterValues.timezone || null,
      merchantId: filterValues?.merchantId
        ? convertReactAdminIdToHydraId(filterValues?.merchantId, Resources.Merchants)
        : null,
      providerIds:
        filterValues?.providerIds?.map((id: string) =>
          convertReactAdminIdToHydraId(id, Resources.Providers),
        ) ?? [],
    },
    resetValues: FILTER_RESET_VALUES,
  });

  useSyncFilter<OrderConversionChartFilterValues>({
    appliedFilters,
    transform: ({ appliedDate, timezone, merchantId, providerIds }) =>
      cleanEmpty({
        appliedDate,
        merchantId: merchantId ? getId(merchantId) : null,
        providerIds: providerIds?.map((id) => getId(id)),
        timezone: getAppliedTimezone(timezone, appliedDate),
      }),
  });

  const merchantWatched = form.watch('merchantId');

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <FilterBar>
          <FilterBarRow sx={{ gridTemplateColumns: '1fr 1fr 1fr 272px' }}>
            <DateTimeRangeTimezoneInput
              label={translate('app.filter.creationTimePeriod')}
              maxDate={new Date()}
              required
              source="appliedDate"
              sx={{ marginBottom: 0 }}
              validate={{
                required: ([from, to]) => (!!from && !!to) || translate('ra.validation.required'),
                periodError: ([from, to]) =>
                  !from ||
                  !to ||
                  differenceInDays(to, from) < 365 ||
                  translate('app.validation.maxPeriod1year'),
              }}
            />
            <ReferenceInput perPage={100} reference="merchants" source="merchantId">
              <AutocompleteInput
                filterToQuery={(searchText: string) => ({ name: searchText })}
                fullWidth
                helperText={false}
                label={translate('app.filter.merchant')}
                onChange={() => form.setValue('providerIds', [])}
                optionText="name"
                validate={required()}
                variant="outlined"
              />
            </ReferenceInput>
            <ReferenceArrayInput
              filter={cleanEmpty({ merchantAccounts: { merchants: merchantWatched } })}
              perPage={100}
              reference="providers"
              sort={{ field: 'name', order: 'ASC' }}
              source="providerIds"
            >
              <AutocompleteMultiselectInput
                filterToQuery={(searchText: string) => ({ name: searchText })}
                helperText={false}
                label={translate('app.filter.providers')}
                name="providerIds"
                optionText="name"
              />
            </ReferenceArrayInput>
            <FilterBarActions>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterBarActions>
          </FilterBarRow>
        </FilterBar>
      </form>
    </FormProvider>
  );
};
